import { i18n } from '@/plugins/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import { useUiStore } from '@/stores/ui.store';
import { handleAxiosError } from '@/utils/error.utils';
import {
  getBuConfigForContext,
  getBuFromDomain,
  getEffectiveBu,
  getLoginRouteNameForPath,
} from '@/utils/bu.utils';
import { getAccountStrategy } from '@/utils/middleware.utils';
import { toast } from '@/utils/toast.utils';
import type { NavigationGuard, RouteLocationNormalized } from 'vue-router';
import {
  DEFAULT_SELECT_ACCOUNT_ROUTE_NAME,
  getBusinessUnitConfig,
} from '@/modules/bu/registry.base';
import { isAdminRole } from '@/utils/role.utils';

const clearAuthSession = (authStore: ReturnType<typeof useAuthStore>) => {
  if (typeof localStorage?.clear === 'function') {
    localStorage.clear();
  }
  authStore.access_token = undefined;
};

// Shared auth flow for Flokee/Cleepo routes to avoid duplicated guard logic.
const ensureAuthenticated = async (
  to: RouteLocationNormalized,
  next: Parameters<NavigationGuard>[2],
  loginRouteName: string,
) => {
  const { t } = i18n.global as any;
  const authStore = useAuthStore();
  const uiStore = useUiStore();

  uiStore.setLoading(true);
  if (!authStore.access_token) {
    uiStore.setLoading(false);
    authStore.lastRoutePath = to.fullPath;
    return next({ name: loginRouteName });
  }

  if (!authStore.user) {
    try {
      await authStore.invalidate();
      await authStore.getCurrentUser();
    } catch (error) {
      clearAuthSession(authStore);
      toast.error({ message: t('notifications.unauthenticated_error') });
      uiStore.setLoading(false);
      authStore.lastRoutePath = to.fullPath;
      return next({ name: loginRouteName });
    }
  }

  uiStore.setLoading(false);
  if (authStore.lastRoutePath) {
    // Prevent double next() when replaying the saved route after authentication.
    const lastRoutePath = authStore.lastRoutePath;
    authStore.lastRoutePath = undefined;
    return next(lastRoutePath);
  } else {
    return next();
  }
};

export const authMiddlewares: Record<string, NavigationGuard> = {
  /**
   * Enforces BU consistency on BU-specific routes (`/flokee/*`, `/cleepo/*`).
   * If route BU and current order BU conflict, send user to the route BU
   * select-account flow (order conflict resolution entry point).
   */
  enforceBusinessUnit: async (_to, _, next) => {
    const resolved_bu = getEffectiveBu({
      path_value: _to.path,
      origin_value: window.location.origin,
      use_stored: true,
    });
    const domain_bu = getBuFromDomain(window.location.origin);

    // In production each BU has a dedicated domain.
    // If route/path BU does not match domain BU, deny access to the route BU namespace.
    if (domain_bu && resolved_bu && domain_bu !== resolved_bu) {
      const { t } = i18n.global as any;
      const domainConfig = getBusinessUnitConfig(domain_bu);
      toast.error({
        message: t('select_account.conflict.domain_bu_mismatch', {
          business_unit_name: domainConfig?.label ?? domain_bu,
        }),
      });
      return next({
        name: domainConfig?.selectAccountRouteName ?? DEFAULT_SELECT_ACCOUNT_ROUTE_NAME,
      });
    }

    const orderSummaryStore = useOrderSummaryStore();
    const order_bu = orderSummaryStore.orderSummary?.business_unit;

    if (order_bu && resolved_bu && order_bu !== resolved_bu) {
      const config = getBusinessUnitConfig(resolved_bu);
      return next({ name: config?.selectAccountRouteName ?? DEFAULT_SELECT_ACCOUNT_ROUTE_NAME });
    }

    next();
  },
  isAppLogin: async (to, _, next) => {
    // If user hits a login page for the wrong BU, redirect to the correct BU login.
    const config = getBuConfigForContext({
      path_value: to.path,
      origin_value: window.location.origin,
      use_stored: true,
    });

    if (config && !to.path.startsWith(config.basePath)) {
      return next({ name: config.loginRouteName });
    }

    next();
  },
  shouldRedirectToDashboard: async (_to, _, next) => {
    const authStore = useAuthStore();
    const uiStore = useUiStore();

    uiStore.setLoading(true);

    if (authStore.access_token) {
      uiStore.setLoading(false);
      // Resolve BU from route/domain to determine the correct dashboard.
      const resolved_bu = getEffectiveBu({
        path_value: _to.path,
        origin_value: window.location.origin,
        use_stored: true,
      });
      const config = getBusinessUnitConfig(resolved_bu);
      if (config) return next({ name: config.dashboardRouteName });
    }

    uiStore.setLoading(false);

    next();
  },
  isAuthenticated: async (_to, _, next) => {
    const loginRouteName = getLoginRouteNameForPath({
      path_value: _to.path,
      origin_value: window.location.origin,
      use_stored: true,
    });

    return ensureAuthenticated(_to, next, loginRouteName);
  },
  isAuthenticatedCleepo: async (_to, _, next) => {
    return ensureAuthenticated(_to, next, 'CleepoAuthLoginView');
  },
  isAccountConfigured: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (!authStore.user) {
      await authStore.logout();
      const loginRouteName = getLoginRouteNameForPath({
        path_value: _to.path,
        origin_value: window.location.origin,
        use_stored: true,
      });
      return next({ name: loginRouteName });
    }

    const strategy = getAccountStrategy(_to);

    // Execute validation steps
    const accountRedirect = await strategy.validateAccount();
    if (accountRedirect) {
      return next({ name: accountRedirect });
    }

    const isInvalidOrder = !authStore.user.current_order_id;

    if (isInvalidOrder) {
      const orderRedirect = await strategy.handleNoCurrentOrder();
      if (orderRedirect) {
        return next({ name: orderRedirect });
      }
    }

    if (strategy.validateIntegration) {
      const integrationRedirect = await strategy.validateIntegration();
      if (integrationRedirect) {
        return next({ name: integrationRedirect });
      }
    }

    return next();
  },
  verifyAuthentication: async (_to, _, next) => {
    const authStore = useAuthStore();
    const uiStore = useUiStore();
    uiStore.setLoading(true);

    if (!authStore.user && authStore.access_token) {
      try {
        await authStore.getCurrentUser();
      } catch (error) {
        const { message } = handleAxiosError(error);
        toast.error({ message });
      }
    }

    uiStore.setLoading(false);

    next();
  },
  isAdmin: async (_to, _, next) => {
    const authStore = useAuthStore();
    const loginRouteName = getLoginRouteNameForPath({
      path_value: _to.path,
      origin_value: window.location.origin,
      use_stored: true,
    });

    if (!authStore.user && authStore.access_token) {
      return next({ name: loginRouteName });
    }

    if (!isAdminRole(authStore.user)) {
      return next({ name: loginRouteName });
    }

    next();
  },
};
